import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@/i18n';
import { api } from '@/api/client';
import type { Child, ReportSummary } from '@/api/types';
import { useSession } from '@/hooks/session';
import { AppShell } from '@/components/layout';
import { Alert, BandPill, Button, ChildChip, Download, EmptyState, Illustration, Plus, RoomTag, Skeleton } from '@/components/ui';
import { ageLabel, formatDate } from '@/lib/format';
import { sceneImage } from '@/lib/illustrations';
import { ActivityCard, ActivityPicture, LogBar, StickerWeek, splitTitle } from '@/features/programme/components';
import { isActive, latestUnlocked, useProgramme } from '@/features/programme/useProgramme';
import { MomentLog } from '@/features/moments/MomentLog';
import { areaChanges } from '@/lib/changes';
import { AboutChildCard, BeforeYouStart, HelpStrip, JourneySteps, LearnPicks, ParentCare, SamplePreview, WelcomeHero } from '@/features/home/HomeExtras';
import type { FamilyAccess } from '@/api/types';

const RECHECK_DAYS = 42;

const greetingKey = (hour: number) => (hour < 12 ? 'home.greet.morning' : hour < 17 ? 'home.greet.afternoon' : 'home.greet.evening');

/** Where a returning parent lands: today's activity first, then the week, results and reports. */
export const FamilyHome = () => {
  const t = useT();
  const { account } = useSession();
  const [children, setChildren] = useState<Child[]>();
  const [reports, setReports] = useState<ReportSummary[]>();
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [access, setAccess] = useState<FamilyAccess[]>([]);

  useEffect(() => {
    Promise.all([api.children.list(), api.reports.list()]).then(
      ([c, r]) => {
        setChildren(c.children);
        setReports(r.reports);
      },
      () => setFailed(true),
    );
    // Families whose reports this parent can view (family access); optional, so failures are quiet.
    api.family?.get().then((f) => setAccess(f.access), () => undefined);
  }, []);

  const active = reports ? latestUnlocked(reports) : undefined;
  const state = useProgramme(active?.assessmentId);
  const week = state.data?.week;
  const pick = week?.todaysPick ? state.blocks.get(week.todaysPick) : undefined;
  const pickActivity = week?.activities.find((a) => a.exerciseId === week.todaysPick);
  const latestSession = useMemo(
    () => (reports && reports.length ? [...reports].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] : undefined),
    [reports],
  );
  const areaResults = useMemo(
    () => (reports && latestSession ? reports.filter((r) => r.assessmentId === latestSession.assessmentId && r.band && r.area) : []),
    [reports, latestSession],
  );
  const changes = useMemo(() => (reports && latestSession ? areaChanges(reports, latestSession.assessmentId) : []), [reports, latestSession]);
  const childOf = (id?: string) => children?.find((c) => c.childId === id);
  const focusChild = childOf(active?.childId ?? latestSession?.childId) ?? children?.[0];
  const firstName = (account?.name ?? '').trim().split(/\s+/)[0];
  const greeting = firstName ? t(greetingKey(new Date().getHours()), { name: firstName }) : t('home.greet.plain');

  if (failed) {
    return (
      <AppShell>
        <Alert tone="warn">{t('prog.loadFailed')}</Alert>
      </AppShell>
    );
  }
  if (!children || !reports) {
    return (
      <AppShell>
        <Skeleton lines={10} />
      </AppShell>
    );
  }

  const sharedWithMe = access.length > 0 && (
    <Link
      to={access.length === 1 ? `/family/${access[0]!.ownerAccountId}` : '/family'}
      className="flex items-center justify-between gap-3 rounded-lg bg-sage-100 px-5 py-3 text-label text-sage-600 hover:bg-sage-200"
    >
      {access.length === 1 ? t('family.reportsOf', { name: access[0]!.ownerName }) : t('home.sharedWithMe', { n: access.length })}
      <span aria-hidden="true">→</span>
    </Link>
  );

  if (children.length === 0) {
    return (
      <AppShell>
        <div className="flex flex-col gap-6">
          <h1 className="text-h3 text-ink-soft">{greeting}</h1>
          {sharedWithMe}
          <WelcomeHero title={t('home.empty.title')} body={t('home.empty.body')} cta={t('home.addChild')} ctaTo="/children" />
          <JourneySteps step={1} />
          <div className="grid gap-6 md:grid-cols-[1.1fr_1fr]">
            <SamplePreview />
            <BeforeYouStart />
          </div>
          <LearnPicks />
          <ParentCare />
          <HelpStrip />
        </div>
      </AppShell>
    );
  }

  const childName = focusChild?.name ?? state.data?.programme.child.name ?? '';
  const reportChild = latestSession?.childName ?? childName;

  const downloadLatest = async () => {
    if (!latestSession) return;
    setDownloading(true);
    try {
      const { url } = await api.reports.download(latestSession.reportId, latestSession.status === 'unlocked' ? 'full' : 'preview');
      window.open(url, '_blank', 'noopener');
    } catch {
      // The report page offers the same download with its own error handling.
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-h1">{greeting}</h1>
          <p className="mt-1 text-body text-ink-soft">
            {week ? t('home.sub.programme', { week: week.programmeWeek, child: childName, days: week.activeDays }) : t('home.sub.none')}
          </p>
        </div>
        <Link to="/children" className="inline-flex min-h-tap items-center gap-2 rounded-md border-[1.5px] border-sage-300 bg-white px-4 text-label font-extrabold text-lagoon-600 hover:border-lagoon-500">
          <Plus className="h-5 w-5" />
          {t('home.addChild')}
        </Link>
      </div>

      {sharedWithMe && <div className="mt-4">{sharedWithMe}</div>}

      {!latestSession && focusChild && (
        <div className="mt-6 flex flex-col gap-6">
          <WelcomeHero
            title={t('home.ready.title', { child: focusChild.name })}
            body={t('home.ready.body')}
            cta={t('home.startCheckin')}
            ctaTo="/children"
          />
          <JourneySteps step={2} />
        </div>
      )}
      {latestSession && latestSession.status !== 'unlocked' && (
        <div className="mt-6">
          <JourneySteps step={3} />
        </div>
      )}

      <div className="mt-6 grid gap-6 nav:grid-cols-[1fr_360px]">
        <div className="flex min-w-0 flex-col gap-6">
          {latestSession && (
            <section aria-labelledby="latest-report" className="relative overflow-hidden rounded-xl bg-lagoon-600 text-white shadow-2 md:grid md:grid-cols-[150px_1fr]">
              <div aria-hidden="true" className="hidden items-end justify-center pt-5 md:flex">
                <div className="h-[150px] w-[124px] overflow-hidden rounded-arch border-[4px] border-b-0 border-coral-500 bg-white">
                  <Illustration src={sceneImage('report-ready-lap')} alt="" position="50% 35%" eager />
                </div>
              </div>
              <div className="p-6 md:pl-2">
                <p className="eyebrow text-coral-300">{t('l2.trust0')}</p>
                <h2 id="latest-report" className="mt-1 text-h2 text-white">
                  {t('home.report.title', { child: reportChild })}
                </h2>
                <p className="mt-1 text-caption text-[#CFE4E1]">
                  {latestSession.status === 'unlocked'
                    ? t('home.report.meta', { date: formatDate(latestSession.createdAt), n: Math.max(areaResults.length, 1) })
                    : t('home.report.preview')}
                </p>
                {areaResults.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {areaResults.map((r) => (
                      <li key={r.reportId} className="flex items-center gap-2 rounded-pill bg-white/10 py-1 pl-3 pr-1 text-caption font-bold">
                        {r.area}
                        <BandPill band={r.band!} className="!py-0.5 !text-caption" />
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button as="a" href={`/reports/${latestSession.reportId}`} variant="celebrate">
                    {t('home.report.open')}
                  </Button>
                  <button
                    type="button"
                    onClick={() => void downloadLatest()}
                    disabled={downloading}
                    className="inline-flex min-h-btn items-center gap-2 rounded-md border-[1.5px] border-white/40 px-4 text-label font-extrabold text-white hover:bg-white/10 disabled:opacity-60"
                  >
                    <Download className="h-5 w-5" />
                    {t('home.report.download')}
                  </button>
                </div>
              </div>
            </section>
          )}

          {active && state.loading && <Skeleton lines={8} />}

          {pick && pickActivity && week ? (
            <section aria-labelledby="today" className="overflow-hidden rounded-xl bg-white shadow-1 md:grid md:grid-cols-[1.05fr_1fr]">
              <div className="h-[220px] md:h-auto md:min-h-[320px]">
                <ActivityPicture exerciseId={pick.exerciseId} pillar={pick.pillar} scene={pick.scene} room={pickActivity.room} className="h-full w-full" />
              </div>
              <div className="flex flex-col gap-3 p-6">
                <p className="eyebrow text-coral-600">
                  {t('home.today', { child: childName })} · {t('home.minutes', { n: pick.durationMinutes })}
                </p>
                <h2 id="today" className="text-h2">{splitTitle(pick.title)[0]}</h2>
                <RoomTag room={pickActivity.room} className="self-start" />
                <ol className="flex flex-col gap-2 text-body text-ink-soft">
                  {pick.steps.slice(0, 3).map((s, i) => (
                    <li key={i} className="grid grid-cols-[28px_1fr] gap-2">
                      <span aria-hidden="true" className="flex h-7 w-7 items-center justify-center rounded-[9px] bg-sage-100 text-caption font-extrabold text-lagoon-600">
                        {i + 1}
                      </span>
                      <span className="line-clamp-2">{s}</span>
                    </li>
                  ))}
                </ol>
                <Link
                  to={`/programme/${active!.assessmentId}/${pick.exerciseId}`}
                  className="text-label font-extrabold text-lagoon-600 underline-offset-4 hover:underline"
                >
                  {t('home.openSteps')} →
                </Link>
              </div>
              <div className="border-t border-line p-4 md:col-span-2">
                <LogBar exerciseId={pick.exerciseId} childName={childName} state={state} compact />
              </div>
            </section>
          ) : !latestSession ? (
            <div className="grid gap-6 md:grid-cols-2">
              <SamplePreview />
              <BeforeYouStart />
            </div>
          ) : (
            !state.loading && (
              <EmptyState
                image={sceneImage('programme-teaser')}
                title={t('home.noProgramme.title')}
                body={t('home.noProgramme.body')}
                action={
                  latestSession ? (
                    <Button as="a" href={`/reports/${latestSession.reportId}`}>
                      {t('home.viewReport')}
                    </Button>
                  ) : (
                    <Button as="a" href="/children">
                      {t('home.startCheckin')}
                    </Button>
                  )
                }
              />
            )
          )}

          {week && active && week.activities.length > 1 && (
            <section aria-labelledby="week">
              <div className="flex items-baseline justify-between gap-3">
                <h2 id="week" className="text-h3">{t('home.week')}</h2>
                <Link to={`/programme/${active.assessmentId}`} className="text-label font-extrabold text-lagoon-600 underline-offset-4 hover:underline">
                  {t('home.seeProgramme')} →
                </Link>
              </div>
              <ul className="mt-3 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {week.activities
                  .filter((a) => a.exerciseId !== week.todaysPick && isActive(a, week))
                  .slice(0, 4)
                  .map((a) => (
                    <li key={a.exerciseId} className="flex">
                      <ActivityCard
                        activity={a}
                        scene={state.blocks.get(a.exerciseId)?.scene}
                        count={week.perActivity[a.exerciseId] ?? 0}
                        isPick={false}
                        to={`/programme/${active.assessmentId}/${a.exerciseId}`}
                      />
                    </li>
                  ))}
              </ul>
            </section>
          )}
        </div>

        <aside className="flex flex-col gap-5">
          {focusChild && (
            <div className="rounded-lg bg-white p-4 shadow-1">
              <ChildChip name={focusChild.name} childId={focusChild.childId} detail={ageLabel(focusChild.ageYears, focusChild.ageMonths)} />
            </div>
          )}
          {focusChild && <AboutChildCard child={focusChild} />}
          {week && <StickerWeek week={week} title={t('home.stickers', { child: childName })} hint={t('home.stickersHint')} />}

          {focusChild && <MomentLog key={focusChild.childId} childId={focusChild.childId} childName={focusChild.name} />}

          {changes.length > 0 && (
            <section aria-labelledby="changes" className="rounded-lg bg-white p-5 shadow-1">
              <h2 id="changes" className="text-[20px]">{t('change.title')}</h2>
              <p className="mt-1 text-body text-ink-muted">{t('change.body', { date: formatDate(changes[0]!.previousDate) })}</p>
              <ul className="mt-3 flex flex-col gap-3">
                {changes.map((c) => (
                  <li key={c.reportId} className="flex flex-col gap-1">
                    <span className="text-label font-extrabold">{c.area}</span>
                    <span className="flex flex-wrap items-center gap-2 text-caption">
                      <BandPill band={c.previousBand} className="!py-0.5 !text-caption" />
                      <span aria-hidden="true">→</span>
                      <BandPill band={c.band} className="!py-0.5 !text-caption" />
                    </span>
                    <span className="text-caption text-ink-soft">{t(`change.${c.change}`)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}


          {latestSession && (
            <section className="rounded-lg bg-lagoon-600 p-5 text-white shadow-2">
              <h2 className="text-[20px] text-white">
                {t('home.nextCheckin', { date: formatDate(new Date(Date.parse(latestSession.createdAt) + RECHECK_DAYS * 86_400_000).toISOString()) })}
              </h2>
              <p className="mt-1 text-body text-[#CFE4E1]">{t('home.nextCheckinBody')}</p>
              <Link to="/children" className="mt-3 inline-flex min-h-tap items-center text-label font-extrabold text-coral-300 underline-offset-4 hover:underline">
                {t('home.startCheckin')} →
              </Link>
            </section>
          )}

          {reports.length > 0 && (
            <section className="rounded-lg bg-white p-5 shadow-1">
              <div className="flex items-baseline justify-between">
                <h2 className="text-[20px]">{t('home.reports')}</h2>
                <Link to="/reports" className="text-caption font-extrabold text-lagoon-600 hover:underline">
                  {t('home.allReports')}
                </Link>
              </div>
              <ul className="mt-2">
                {[...reports]
                  .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                  .slice(0, 4)
                  .map((r) => (
                    <li key={r.reportId}>
                      <Link to={`/reports/${r.reportId}`} className="grid min-h-tap grid-cols-[12px_1fr_auto] items-center gap-3 py-1.5 text-label hover:text-lagoon-600">
                        <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${r.status === 'unlocked' ? 'bg-sage-500' : 'bg-sage-300'}`} />
                        <span className="truncate">
                          {r.childName}
                          {r.area ? ` · ${r.area}` : ''}
                        </span>
                        <span className="text-caption text-ink-muted">{formatDate(r.createdAt)}</span>
                      </Link>
                    </li>
                  ))}
              </ul>
            </section>
          )}
        </aside>
      </div>

      <div className="mt-10 flex flex-col gap-6">
        <LearnPicks child={focusChild} />
        <ParentCare />
        <HelpStrip />
      </div>
    </AppShell>
  );
};
